import React, { Fragment, useState, useEffect, useRef, useContext } from "react"

import { useHistory, useParams } from "react-router-dom"
import _ from "lodash"
import * as Utils from "../../components/Utils"

import AvailabilitySelector, {
  IAvailabilitySelectorValues
} from "../../components/AvailabilitySelector"

import {
  updateSlotAvailabilitySelection,
  IFieldValues
} from "../../containers/selectionSlice"

import { Season, Seasons } from "../../api/Seasons"

import {
  AirportSeasonConfigurations,
  AirportSeasonConfiguration
} from "../../api/AirportSeasonConfiguration"

import Button from "../../components/generic/buttons/Button"
import { getAirportSeasonConfigurations } from "../../api/BackendCalls"
import { PageId } from "../../common/pageId"

import { getPageConfig } from "./Config"

import { CommonText, FieldName, FormText, LoadingText } from "../../common/text"
import useErrorHandling from "../../hooks/useErrorHandling"
import { EditMode } from "../../common/transaction"
import SearchCriteria from "../../components/SearchCriteria"
import { AppContext } from "../../app/App"
import { handleGetSlotAvailability } from "../Support"
import SelectorPanel from "../../components/FlightSelectorPanel"
import { JsonErrorCode } from "../../api/Communication"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { useAvailabilitySelectorStateValues } from "../../hooks/useFlighSelectorInitValues"

interface Props {
  pageId: PageId
}
interface ITime {
  time: string
  date: string
  adt: string
}

export const SlotAvailability: React.FunctionComponent<Props> = ({
  pageId
}) => {
  const dispatch = useAppDispatch()
  const history = useHistory()
  const params = useParams<ITime>()
  const { season, countries, airports, useLocalTime, useICAOCode } =
    useAppSelector(
      (state) => state["selectionReducer"].airportSeasonsSelector
    ) || {}

  const appContext = useContext(AppContext)
  const slotAvailabilitySelector = useAppSelector(
    (state) => state["selectionReducer"].slotAvailabilitySelector
  )
  const { pinSelectorPanel } = useAppSelector(
    (state) => state["selectionReducer"].userSettings
  )

  const [airportSeasons, setAirportSeasons] =
    useState<AirportSeasonConfigurations>([])

  // get configuration for selected season and airports
  const getConfiguration = (): AirportSeasonConfiguration => {
    const airportSeasonsClone = _.cloneDeep(airportSeasons)
    const configs: AirportSeasonConfigurations = _.filter(
      airportSeasonsClone,
      (rec) => {
        return (
          rec.season === season &&
          _.findIndex(airports, (a) => {
            return a === rec.airport
          }) !== -1 &&
          (useICAOCode ? rec.supportsIcao : rec.supportsIata)
        )
      }
    )

    if (configs.length > 0) {
      let actualConfig: AirportSeasonConfiguration = configs[0]

      actualConfig.supportsQuotaOverlay = configs.find(
        (c) => c.supportsQuotaOverlay
      )
        ? true
        : false

      actualConfig.terminals = _.chain(configs)
        .reduce((result, item) => {
          return _.intersection(result, item.terminals)
        }, configs[0].terminals)
        .uniq()
        .sort()
        .value()

      return actualConfig
    } else {
      return null
    }
  }

  const availabilitySelectorValues = useAvailabilitySelectorStateValues()

  const [terminal, setTerminal] = useState<string>(
    slotAvailabilitySelector.values["terminal"]
  )
  const config = getConfiguration()
  const supportsQuotaOverlay = config?.supportsQuotaOverlay
  const supportsQuotaConstraints = config?.supportsQuotaConstraints

  const [constraintType, setConstraintType] = useState<string>(
    slotAvailabilitySelector.values["constraintType"] || "Runway"
  )
  const [valueType, setValueType] = useState<string>(
    slotAvailabilitySelector.values["valueType"] || "availability"
  )
  const [operators, setOperators] = useState<string[]>(
    slotAvailabilitySelector.values["operators"] || []
  )

  const [adt, setAdt] = useState<string>(slotAvailabilitySelector.values["adt"])
  const [dayOfWeek, setDayOfWeek] = useState<string>(
    slotAvailabilitySelector.values["dayOfWeek"]
  )
  const [hour, setHour] = useState<string>(
    slotAvailabilitySelector.values["hour"] || "00"
  )
  const [date, setDate] = useState<string>(
    slotAvailabilitySelector.values["date"] || null
  )
  const [useHourlyConstraints, setUseHourlyConstraints] = useState<boolean>(
    slotAvailabilitySelector.values["useHourlyConstraints"] !== undefined
      ? slotAvailabilitySelector.values["useHourlyConstraints"]
      : true
  )
  const [mergeTotals, setMergeTotals] = useState<boolean>(
    slotAvailabilitySelector.values["mergeTotals"] !== undefined
      ? slotAvailabilitySelector.values["mergeTotals"]
      : true
  )
  const [schedule, setSchedule] = useState(
    slotAvailabilitySelector.values["schedule"] || "live"
  )

  const [touched, setTouched] = useState(false)
  const [overlayQuotaConstraints, setOverlayQuotaConstraints] =
    useState<boolean>(
      supportsQuotaOverlay === false
        ? false
        : slotAvailabilitySelector.values["overlayQuotaConstraints"] ?? false
    )
  const [collapsedSidepanel, setCollapsedSidepanel] = useState(false)
  const [NextPrevRequest, setNextPrevRequest] = useState<boolean>(false)
  const pageConfig = getPageConfig(config, NextPrevRequest, constraintType)
  const pageMap = pageConfig[pageId]
  const codeType = pageMap.codeType
  const pageTitle = pageMap.pageTitle
  const airportseasonSelectorConfig = pageMap.airportseasonSelectorConfig
  const flightSelectorConfig = pageMap.flightSelectorConfig
  const excludeFields = flightSelectorConfig
  const showNextPrevDayBtns =
    pageId === PageId.AvailSeasonHour || pageId === PageId.DetailSeason

  useEffect(() => {
    if (supportsQuotaOverlay === false) {
      setOverlayQuotaConstraints(false)
    }
    // eslint-disable-next-line
  }, [airports])

  useEffect(() => {
    if (supportsQuotaConstraints === false) {
      setOverlayQuotaConstraints(false)
    }
    // eslint-disable-next-line
  }, [constraintType])

  useEffect(() => {
    if (pageId === PageId.AvailBaseline && config) {
      setDate(config.peakWeekDate)
    }
    if (
      pageId === PageId.AvailWeekHour ||
      pageId === PageId.AvailWeekOOH ||
      pageId === PageId.AvailSeasonHour
    ) {
      setNextPrevRequest(true)
    }
    // eslint-disable-next-line
  }, [airports, season])

  const [matrixAsHtml, setMatrixAsHtml] = useState("")

  const seasons: Seasons = useAppSelector(
    (state) => state["appReducer"].seasons
  )
  const selectedSeason: Season = seasons.find((sd) => sd.code === season)

  const usePrevious = (value: string) => {
    const ref = useRef<string>()
    useEffect(() => {
      ref.current = value
    })
    return ref.current
  }
  const prevPageId = usePrevious(pageId)

  useEffect(() => {
    if (!pinSelectorPanel) {
      setCollapsedSidepanel(false)
    }

    if (
      pageId !== PageId.DetailWeek &&
      pageId !== PageId.DetailWeekOOH &&
      pageId !== PageId.DetailSeason &&
      prevPageId !== PageId.DetailWeek &&
      prevPageId !== PageId.DetailWeekOOH &&
      prevPageId !== PageId.DetailSeason
    ) {
      setDate(slotAvailabilitySelector.values["date"] || null)
      setTerminal(slotAvailabilitySelector.values["terminal"])
      setDayOfWeek(slotAvailabilitySelector.values["dayOfWeek"])
      setAdt(slotAvailabilitySelector.values["adt"])
      setHour(slotAvailabilitySelector.values["hour"] || "00")
      setUseHourlyConstraints(
        slotAvailabilitySelector.values["useHourlyConstraints"] !== undefined
          ? slotAvailabilitySelector.values["useHourlyConstraints"]
          : true
      )
      setMergeTotals(
        slotAvailabilitySelector.values["mergeTotals"] !== undefined
          ? slotAvailabilitySelector.values["mergeTotals"]
          : true
      )
      setOverlayQuotaConstraints(
        supportsQuotaOverlay === false
          ? false
          : slotAvailabilitySelector.values["overlayQuotaConstraints"] ?? false
      )
    }
    setMatrixAsHtml("")
    appContext.handleSetIsLoading({
      isLoading: true,
      loadingTxt: LoadingText.loadingAirportSeasons
    })
    getAirportSeasonConfigurations(
      pageId,
      (airportSeasons) => {
        setAirportSeasons(airportSeasons)
        appContext.handleSetIsLoading({ isLoading: false })
      },
      (errorObj) => {
        appContext.handleSetIsLoading({ isLoading: false, fromFailure: true })
        if (errorObj && errorObj.code === JsonErrorCode.AuthenticationError) {
          history.push("/")
        }
      }
    )
    setNextPrevRequest(false)
    // eslint-disable-next-line
  }, [pageId])

  useEffect(() => {
    let newHour = hour
    let newDate: Date = new Date(date)
    let newAdt = adt
    if (
      excludeFields.nextPrev !== true ||
      pageId === PageId.DetailWeek ||
      pageId === PageId.DetailWeekOOH ||
      pageId === PageId.DetailSeason
    ) {
      if (params.time && params.time.length > 0) {
        newHour = params.time.slice(0, 2)
        setHour(newHour)
        dispatch(
          updateSlotAvailabilitySelection({
            field: FieldName.hour,
            value: newHour
          })
        )
      }
      if (params.date && params.date.length > 0 && selectedSeason && season) {
        newDate = Utils.GetValidDateInSeason(
          selectedSeason.startDate,
          selectedSeason.endDate,
          params.date
        )
        if (newDate != null) {
          const dayOfWeek = newDate.getDay() || 7
          setDayOfWeek(dayOfWeek.toString())
          setDate(Utils.formatDate(newDate))
          dispatch(
            updateSlotAvailabilitySelection({
              field: FieldName.date,
              value: Utils.formatDate(newDate)
            })
          )
          dispatch(
            updateSlotAvailabilitySelection({
              field: FieldName.dayOfWeek,
              value: dayOfWeek.toString()
            })
          )
        }
        if (params.adt && params.adt.length > 0) {
          newAdt = params.adt
          setAdt(newAdt)
          dispatch(
            updateSlotAvailabilitySelection({
              field: FieldName.adt,
              value: newAdt
            })
          )
        }
      }
      setSendRequest(true)
    }
    // eslint-disable-next-line
  }, [params.adt, params.time, params.date])

  const [sendRequest, setSendRequest] = useState(false)

  const prevHourState = usePrevious(hour)

  const prevDayOfWeekState = usePrevious(dayOfWeek)

  const prevDate = usePrevious(date)

  const prevAdt = usePrevious(adt)

  useEffect(() => {
    if (
      sendRequest && //next prev goDetail
      (dayOfWeek !== prevDayOfWeekState ||
        hour !== prevHourState ||
        adt !== prevAdt ||
        date !== prevDate)
    ) {
      callSlotAvailability()
    }
    // eslint-disable-next-line
  }, [pageId, sendRequest, hour, adt, date])

  useEffect(() => {
    if (appContext.pressedGoBack) {
      callSlotAvailability()
      appContext.handleChangePressedGoBack(false)
    }
    // eslint-disable-next-line
  }, [])

  const emptyMatrix = () => {
    setMatrixAsHtml("")
  }

  const callSlotAvailability = () => {
    appContext.handleSetIsLoading({
      isLoading: true,
      loadingTxt: "Fetching availability..."
    })
    setMatrixAsHtml("")
    const param = getVisibleParams()
    const airport = airports.length > 0 ? airports[0] : ""
    setSendRequest(false)
    const useBaseline = schedule === "baseline"
    getSlotAvailability1({
      availabilityType: {
        constraintType: constraintType,
        valueType: valueType
      },
      useIcaoCode: useICAOCode,
      useLocalTime: useLocalTime,
      useBaseline: useBaseline,
      scope: {
        season: season,
        airport: airport,
        terminal: param.terminal,
        operators: operators || [],
        adt: param.adt,
        date: param.date,
        hour: param.hour,
        dayOfWeek: param.dayOfWeek,
        useHourlyConstraints: param.useHourlyConstraints,
        mergeTotals: param.mergeTotals,
        overlayQuotaConstraints: param.overlayQuotaConstraints
      }
    })
  }

  async function getSlotAvailability1(parameters: any) {
    if (parameters === null || parameters.scope === null) {
      history.goBack()
    }
    if (
      parameters &&
      parameters.scope &&
      parameters.scope.date === null &&
      excludeFields.date !== true
    ) {
      appContext.handleSetIsLoading({ isLoading: false })
      return
    }

    handleGetSlotAvailability(
      pageId,
      parameters,
      (matrix: string) => {
        setMatrixAsHtml(matrix)
        appContext.handleSetIsLoading({ isLoading: false })

        if (!pinSelectorPanel) {
          setCollapsedSidepanel(true)
        }
      },
      () => {
        appContext.handleSetIsLoading({ isLoading: false, fromFailure: true })
      }
    )
  }

  const getValues = (): IAvailabilitySelectorValues => {
    return {
      constraintType: constraintType,
      valueType: valueType,
      operators: operators,
      terminal: terminal,
      adt: adt,
      dayOfWeek: dayOfWeek,
      hour: hour,
      date: date,
      useHourlyConstraints: useHourlyConstraints,
      mergeTotals: mergeTotals,
      overlayQuotaConstraints: overlayQuotaConstraints,
      schedule: schedule
    }
  }

  useEffect(() => {
    const availablityStateValues = getValues()
    if (appContext.userChanged) {
      if (!_.isEqual(availablityStateValues, availabilitySelectorValues)) {
        setConstraintType(availabilitySelectorValues.constraintType)
        setValueType(availabilitySelectorValues.valueType)
        setTerminal(availabilitySelectorValues.terminal)
        setOperators(availabilitySelectorValues.operators)
        setAdt(availabilitySelectorValues.adt)
        setDayOfWeek(availabilitySelectorValues.dayOfWeek)
        setHour(availabilitySelectorValues.hour)
        setDate(availabilitySelectorValues.date)
        setUseHourlyConstraints(availabilitySelectorValues.useHourlyConstraints)
        setMergeTotals(availabilitySelectorValues.mergeTotals)
        setOverlayQuotaConstraints(
          availabilitySelectorValues.overlayQuotaConstraints
        )
        setSchedule(availabilitySelectorValues.schedule)
      }
    }
    appContext.handleChangeUserChanged(false)
    //eslint-disable-next-line
  }, [slotAvailabilitySelector])

  // TODO: use context provider to replace hookMaps ?
  const hookMaps = {
    constraintType: setConstraintType,
    valueType: setValueType,
    operators: setOperators,
    airportSeasons: setAirportSeasons,
    terminal: setTerminal,
    adt: setAdt,
    dayOfWeek: setDayOfWeek,
    hour: setHour,
    date: setDate,
    useHourlyConstraints: setUseHourlyConstraints,
    mergeTotals: setMergeTotals,
    overlayQuotaConstraints: setOverlayQuotaConstraints,
    schedule: setSchedule
  }

  const [fieldName, setFieldName] = useState("")
  const [error, setError] = useState(false)
  const selectedAirportsObj = {
    airportSeasons,
    airports,
    countries,
    season,
    useLocalTime
  }

  const isMulti = airportseasonSelectorConfig?.dropdownMultiSelect
  const { errors, invalidFieldsArrs, errorMsg } = useErrorHandling(
    pageId,
    selectedAirportsObj,
    getValues(),
    fieldName,
    error,
    isMulti,
    EditMode.AVAILABILITY
  )

  const handleChange = (field: string, val: string, error: boolean) => {
    setFieldName(field)
    setError(error)
    setTouched(false)
    const hook = hookMaps[field]
    if (typeof hook === "function") {
      hook(val)
    }
  }

  const handleDayOfWeekChange = (updDayOfWeek: string) => {
    setDayOfWeek(updDayOfWeek)
    dispatch(
      updateSlotAvailabilitySelection({
        field: FieldName.dayOfWeek,
        value: updDayOfWeek
      })
    )
    setSendRequest(true)
  }

  const prevDay = () => {
    let dayOfWeekCount = parseInt(dayOfWeek)
    if (dayOfWeekCount > 1) {
      dayOfWeekCount--
    } else {
      dayOfWeekCount = 7
    }

    handleDayOfWeekChange(dayOfWeekCount.toString())
  }

  const nextDay = () => {
    let dayOfWeekCount = parseInt(dayOfWeek)
    if (dayOfWeekCount < 7) {
      dayOfWeekCount++
    } else {
      dayOfWeekCount = 1
    }

    handleDayOfWeekChange(dayOfWeekCount.toString())
  }

  const prevHour = () => {
    if (hour && hour.length > 0) {
      let h = parseInt(hour)
      if (h > 0) {
        h--
        const newHour = h.toString().replace(/^([0-9])$/, "0$1")
        setHour(newHour)
        dispatch(
          updateSlotAvailabilitySelection({
            field: FieldName.hour,
            value: newHour
          })
        )
        setSendRequest(true)
      }
    }
  }

  const nextHour = () => {
    if (hour && hour.length > 0) {
      let h = parseInt(hour)
      if (h < 23) {
        h++
        const newHour = h.toString().replace(/^([0-9])$/, "0$1")
        setHour(newHour)
        dispatch(
          updateSlotAvailabilitySelection({
            field: FieldName.hour,
            value: newHour
          })
        )
        setSendRequest(true)
      }
    }
  }

  const getVisibleParams = () => {
    return {
      terminal: terminal !== null ? terminal : "",
      dayOfWeek: excludeFields.dayOfWeek === true ? "1" : dayOfWeek,
      adt: excludeFields.adt === true ? "L" : adt,
      hour: excludeFields.hour === true ? "" : hour,
      date: excludeFields.date === true ? null : date,
      useHourlyConstraints:
        excludeFields.useHourlyConstraints === true
          ? false
          : useHourlyConstraints,
      mergeTotals: excludeFields.mergeTotals === true ? false : mergeTotals,
      overlayQuotaConstraints:
        excludeFields.overlayQuotaConstraints === true
          ? false
          : overlayQuotaConstraints,
      schedule: schedule
    }
  }

  const handleSubmitUpdate = () => {
    const param = getVisibleParams()
    const invalidArr = Object.values(errors).filter((a) => a === true)
    const sendSubmit = invalidFieldsArrs.length === 0 && invalidArr.length === 0
    setTouched(true)

    if (
      pageId === PageId.AvailWeekHour ||
      pageId === PageId.AvailWeekOOH ||
      pageId === PageId.AvailSeasonHour
    ) {
      setNextPrevRequest(true)
    }

    if (sendSubmit) {
      callSlotAvailability()
      saveFilterParameters({
        constraintType: constraintType,
        valueType: valueType,
        season: season,
        countries: countries,
        airport: airports,
        operators: operators,
        terminal: param.terminal,
        adt: param.adt,
        date: param.date,
        hour: param.hour,
        dayOfWeek: param.dayOfWeek,
        useHourlyConstraints: param.useHourlyConstraints,
        mergeTotals: param.mergeTotals,
        overlayQuotaConstraints: param.overlayQuotaConstraints,
        schedule: config && config.supportsBaseline ? param.schedule : false
      })
    }
  }

  async function saveFilterParameters(parameters: IFieldValues) {
    _.each(_.keys(parameters), (key) => {
      dispatch(
        updateSlotAvailabilitySelection({
          field: FieldName[key],
          value: parameters[key]
        })
      )
    })
  }

  return (
    <Fragment>
      <article className="ocs-page-container ocs-slot-availability d-flex">
        <SelectorPanel
          title={pageTitle}
          isCollapsed={collapsedSidepanel}
          handleToggleCollapsed={setCollapsedSidepanel}
        >
          <form
            className="ocs-form ocs-flight-selector-container"
            onSubmit={(e) => e.preventDefault()}
          >
            <AvailabilitySelector
              title={pageTitle}
              airportSeasons={airportSeasons}
              values={getValues()}
              onChange={handleChange}
              airportseasonSelectorConfig={airportseasonSelectorConfig}
              excludeFields={excludeFields}
              codeType={codeType}
              touched={touched}
              errorMsg={errorMsg}
              hasError={
                errors.disabledOrlockedError ||
                errors.emptyAirportField ||
                errors.isAvailDisabledError
              }
              emptyMatrix={emptyMatrix}
            />
            <div className="submit-container d-flex justify-content-end">
              <Button
                onClick={handleSubmitUpdate}
                variant="primary"
                type="submit"
                panelBtn
                className="mq-btn-height"
              >
                {FormText.upDate}
              </Button>
            </div>
          </form>
        </SelectorPanel>

        {matrixAsHtml !== "" && (
          <div className="ocs-sidepanel-right-content overflow ocs-pt-20px">
            <div className="page-content-wrapper slot-availability-wrapper">
              <SearchCriteria
                editMode={EditMode.AVAILABILITY}
                excludeConfig={excludeFields}
                airportSeasons={airportSeasons}
                useICAOCode={useICAOCode}
              />
              <div className="ocs-mt-16px ocs-mb-12 d-flex slot-availability-btns">
                {flightSelectorConfig?.goBack ? null : (
                  <Button
                    className="mq-btn-height"
                    onClick={() => {
                      setMatrixAsHtml("")
                      setSendRequest(true)
                      history.goBack()
                      appContext.handleChangePressedGoBack(true)
                    }}
                  >
                    {CommonText.goBack}
                  </Button>
                )}
                {flightSelectorConfig?.nextPrev ? null : (
                  <div className="d-flex flex-1 justify-content-center">
                    {showNextPrevDayBtns && (
                      <div className="btn-margin">
                        <Button className="mq-btn-height" onClick={prevDay}>
                          {CommonText.prevDay}
                        </Button>
                      </div>
                    )}
                    <div className="btn-margin">
                      <Button className="mq-btn-height" onClick={prevHour}>
                        {CommonText.prevHour}
                      </Button>
                    </div>
                    <div className={showNextPrevDayBtns ? "btn-margin" : ""}>
                      <Button className="mq-btn-height" onClick={nextHour}>
                        {CommonText.nextHour}
                      </Button>
                    </div>
                    {showNextPrevDayBtns && (
                      <Button className="mq-btn-height" onClick={nextDay}>
                        {CommonText.nextDay}
                      </Button>
                    )}
                  </div>
                )}
              </div>
              <section className="ocs-matrix-container">
                <div
                  className="overflow ocs-slot-availability-matrix"
                  dangerouslySetInnerHTML={{ __html: matrixAsHtml }}
                />
              </section>
            </div>
          </div>
        )}
      </article>
    </Fragment>
  )
}
